// Every page, in sidebar order (the list itself, with labels, sections and roles, is nav.ts). The sidebar, command
// palette and page switcher all read `routes`. Each path gets its icon and page here; TypeScript refuses a path in
// nav.ts that has none. A route without `page` shows a "coming in phase N" placeholder.

import {
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Factory,
  FileArchive,
  FolderKanban,
  KeyRound,
  Landmark,
  Layers,
  LayoutDashboard,
  type LucideIcon,
  Mail,
  Package,
  PackageCheck,
  Puzzle,
  Receipt,
  ScrollText,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Store,
  TrendingUp,
  Truck,
  Users,
  Warehouse,
  Workflow,
} from "lucide-react";
import type { ComponentType } from "react";
import { matchNav, NAV, type NavDef, type NavPath } from "./nav.ts";
import { BomsPage } from "./pages/BomsPage.tsx";
import { BillsPage } from "./pages/bills/BillsPage.tsx"; // Bills and payments ---
import { BookkeeperPage } from "./pages/bills/BookkeeperPage.tsx";
import { ConsignmentPage } from "./pages/consignment/ConsignmentPage.tsx"; // Consignment ---
import { CountsPage } from "./pages/counts/CountsPage.tsx"; // Stock counts ---
import { EmailSettingsPage } from "./pages/EmailSettingsPage.tsx"; // PO email ---
import { ForecastPage } from "./pages/ForecastPage.tsx"; // Forecasting ---
import { HomePage } from "./pages/HomePage.tsx";
import { ItemsPage } from "./pages/ItemsPage.tsx";
import { McpPage } from "./pages/McpPage.tsx";
import { MakePlanPage } from "./pages/make/MakePlanPage.tsx"; // Make planning ---
import { PayAccountsPage } from "./pages/PayAccountsPage.tsx";
import { PurchasingPlanPage } from "./pages/PurchasingPlanPage.tsx";
import { PurchaseOrdersPage } from "./pages/po/PurchaseOrdersPage.tsx";
import { ProjectsPage } from "./pages/projects/ProjectsPage.tsx"; // Manufacturing projects ---
import { ReceivingPage } from "./pages/receiving/ReceivingPage.tsx"; // Receiving ---
import { SettingsIndex, withSettingsTabs } from "./pages/SettingsPage.tsx";
import { ShopifyPage } from "./pages/ShopifyPage.tsx";
import { ProductionDocsPage } from "./pages/specs/ProductionDocsPage.tsx"; // Specs ---
import { UsersPage } from "./pages/UsersPage.tsx";
import { VendorsPage } from "./pages/VendorsPage.tsx";
import { WiseSettingsPage } from "./pages/WiseSettingsPage.tsx"; // Bills and payments ---

const SellItemsPage = () => <ItemsPage kind="sell" title="Sell items" />;
const PartsPage = () => <ItemsPage kind="part" title="Parts library" />; // Manufacturing projects --- materials, sub-assemblies, services

export type RouteDef = NavDef & { icon: LucideIcon; page?: ComponentType };

const PAGES: Record<NavPath, { icon: LucideIcon; page?: ComponentType }> = {
  "/": { icon: LayoutDashboard, page: HomePage },
  "/items/sell": { icon: Store, page: SellItemsPage },
  "/forecast": { icon: TrendingUp, page: ForecastPage }, // Forecasting ---
  "/purchasing": { icon: ShoppingCart, page: PurchasingPlanPage }, // Planning ---
  "/pos": { icon: ClipboardList, page: PurchaseOrdersPage }, // Purchase orders ---
  "/receiving": { icon: PackageCheck, page: ReceivingPage }, // Receiving ---
  "/counts": { icon: ClipboardCheck, page: CountsPage }, // Stock counts ---
  "/vendors": { icon: Truck, page: VendorsPage },
  "/projects": { icon: FolderKanban, page: ProjectsPage }, // Manufacturing projects --- the list, and /projects/<code>
  "/make": { icon: Factory, page: MakePlanPage }, // Make planning ---
  "/consignment": { icon: Warehouse, page: ConsignmentPage }, // Consignment ---
  "/specs": { icon: ScrollText, page: ProductionDocsPage }, // Specs ---
  "/boms": { icon: Layers, page: BomsPage }, // BOMs ---
  "/parts": { icon: Puzzle, page: PartsPage }, // Manufacturing projects ---
  "/bills": { icon: Receipt, page: BillsPage }, // Bills and payments ---
  "/bookkeeper": { icon: FileArchive, page: BookkeeperPage },
  "/settings": { icon: Settings, page: SettingsIndex },
  "/settings/users": { icon: Users, page: withSettingsTabs(UsersPage) },
  "/settings/shopify": { icon: ShoppingBag, page: withSettingsTabs(ShopifyPage) },
  "/settings/email": { icon: Mail, page: withSettingsTabs(EmailSettingsPage) }, // PO email ---
  "/settings/wise": { icon: Landmark, page: withSettingsTabs(WiseSettingsPage) }, // Bills and payments ---
  "/settings/payment-accounts": { icon: CreditCard, page: withSettingsTabs(PayAccountsPage) }, // Which card ---
  "/settings/mcp": { icon: KeyRound, page: withSettingsTabs(McpPage) },
  // Old addresses (redirects in nav.ts).
  "/items/materials": { icon: Package },
  "/flow": { icon: Workflow }, // Product flow --- a project's tab now
  "/items/subassemblies": { icon: Package },
};

export const routes: RouteDef[] = NAV.map((n) => ({ ...n, ...PAGES[n.path] }));

export function matchRoute(path: string): RouteDef | undefined {
  return matchNav(routes, path);
}
